import React, { Suspense, lazy, useCallback, useContext, useMemo, useState } from "react";
import { Archive, Trash2, Zap } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderTrigger from "./api";
import { loadTriggerFilterDependencies } from "./features/trigger.dependencies";
import { triggerFilterFields } from "./features/trigger.filter";
import { getTriggerColumns } from "./trigger-list/triggerColumns";
import TriggerRowActions from "./trigger-list/TriggerRowActions";
import TriggerViewDrawer from "./trigger-dialogs/TriggerViewDrawer";
import TriggerUsedByDrawer from "./trigger-dialogs/TriggerUsedByDrawer";

// The editor is only needed once opened.
const TriggerEditor = lazy(() => import("./trigger-editor/TriggerEditor"));

// Reminder Library -> Trigger (tbl_rem_trigger): what a reminder detects, reusable across rules.
const Trigger = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderTrigger.getAll, { loadError: "Failed to load triggers.", initialSort: { key: "id", direction: "desc" } });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [editor, setEditor] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [usedBy, setUsedBy] = useState(null);
  const [busy, setBusy] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /** Runs a row request with its busy spinner. */
  const withBusy = async (row, action, run) => {
    setBusy({ id: row.id, action });
    try {
      await run();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const openView = (row) =>
    withBusy(row, "view", async () => {
      const res = await apiReminderTrigger.getById(row.id);
      setViewRecord(res.data?.data ?? null);
    });

  const openUsedBy = (row) =>
    withBusy(row, "used-by", async () => {
      const res = await apiReminderTrigger.getUsedBy(row.id);
      setUsedBy({ trigger: row, data: res.data?.data ?? null });
    });

  const handleClone = (row) =>
    withBusy(row, "clone", async () => {
      const res = await apiReminderTrigger.clone(row.id);
      showAlert({ type: "success", message: res.data?.message || "Copied as a draft." });
      list.reload();
    });

  const handlePublish = (row) =>
    withBusy(row, "publish", async () => {
      try {
        const res = await apiReminderTrigger.publish(row.id);
        showAlert({ type: "success", message: res.data?.message || "Published." });
      } catch (err) {
        const problems = err.response?.data?.data?.problems ?? [];
        const more = problems.length > 1 ? ` (+${problems.length - 1} more, see View)` : "";
        showAlert({ type: "error", title: "Cannot publish yet", message: `${errorMessage(err)}${more}` });
      } finally {
        // A refused publish stores its problems on the trigger, so the list refreshes either way.
        list.reload();
      }
    });

  const handleArchive = async (row) => {
    const confirmed = await openConfirm({
      title: "Archive Trigger",
      message: `Archive "${row.name}"? It is no longer offered to new rules. A trigger used by an active rule can't be archived.`,
      confirmText: "Archive",
      variant: "warning",
      icon: <Archive size={20} />,
    });
    if (!confirmed) return;
    await withBusy(row, "archive", async () => {
      const res = await apiReminderTrigger.archive(row.id);
      showAlert({ type: "success", message: res.data?.message || "Archived." });
      list.reload();
    });
  };

  const deleteTriggers = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Trigger",
      message: `Delete ${ids.length > 1 ? `${ids.length} triggers` : "this trigger"}? Triggers used by rules or templates can't be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderTrigger.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const { reload } = list;
  const closeEditor = useCallback(
    (changed) => {
      setEditor(null);
      if (changed) reload();
    },
    [reload]
  );

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getTriggerColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <TriggerRowActions
      row={row}
      canManage={canManage}
      busy={busy}
      onView={openView}
      onUsedBy={openUsedBy}
      onEdit={(target) => setEditor({ id: target.id })}
      onClone={handleClone}
      onPublish={handlePublish}
      onArchive={handleArchive}
      onDelete={(target) => deleteTriggers([target.id])}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setEditor({ id: null })}
          onDeleteSelected={canManage ? () => deleteTriggers(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={triggerFilterFields} dependenciesLoader={loadTriggerFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Zap}
              title={list.hasQuery ? "No triggers match" : "No reminder triggers yet"}
              message={
                list.hasQuery
                  ? "No triggers match your search or filters. Try adjusting them."
                  : "A trigger is what a reminder watches for: records that match conditions, a date coming up, an event, or a scheduled broadcast."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Create Trigger"
              onCreate={() => setEditor({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <Suspense fallback={null}>{editor && <TriggerEditor triggerId={editor.id} onClose={closeEditor} />}</Suspense>

      {viewRecord && <TriggerViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {usedBy && <TriggerUsedByDrawer trigger={usedBy.trigger} usedBy={usedBy.data} onClose={() => setUsedBy(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Trigger;
